import { createApi } from "@reduxjs/toolkit/query/react";
import { CalculationRowData, MinesFeeResponse } from "interfaces/Revenue";
import { customBaseQuery, handleQueryLifecycle } from "store/utils";

type Id = string | number;

export interface FeeCalculationListResponse {
  data: CalculationRowData[];
  totalCount: number;
}

export interface FeeCalculationListParams {
  portfolio_id?: string;
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const feeCalculationApi = createApi({
  reducerPath: "feeCalculationApi",
  baseQuery: customBaseQuery,
  tagTypes: ["FeeCalculations", "FeeCalculation"],
  endpoints: (builder) => ({
    getFeeCalculations: builder.query<
      FeeCalculationListResponse,
      FeeCalculationListParams
    >({
      query: (params) => ({
        url: "/api/revenue/calculation-history/",
        params,
      }),
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: CalculationRowData) => ({
                type: "FeeCalculation" as const,
                id: item.id,
              })),
              { type: "FeeCalculations" as const, id: "LIST" },
            ]
          : [{ type: "FeeCalculations" as const, id: "LIST" }];
      },
    }),

    getFeeCalculationById: builder.query<any, string | number>({
      query: (id) => `/api/revenue/calculation-history/${id}/`,
      providesTags: (_res, _err, id) => [{ type: "FeeCalculation", id }],
    }),

    deleteFeeCalculation: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/calculation-history/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeeCalculations", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    createFeeCalculation: builder.mutation<any, any>({
      query: (body) => ({
        url: "/api/revenue/fee-calculation/",
        method: "POST",
        body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    saveFeeCalculation: builder.mutation<any, any>({
      query: (body) => ({
        url: "/api/revenue/calculation-history/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeeCalculations", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    createProforma: builder.mutation<
      any,
      { data: any; isDirectProforma: boolean; calculationId?: string }
    >({
      query: ({ data, isDirectProforma, calculationId }) => {
        if (isDirectProforma) {
          return {
            url: "/api/revenue/proforma-history/",
            method: "POST",
            body: data,
          };
        }
        return {
          url: `/api/revenue/calculation-history/${calculationId}/proforma-convert/`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "FeeCalculations", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
      },
    }),

    minesFee: builder.mutation<
      MinesFeeResponse,
      { portfolio: string; startDate: string; endDate: string }
    >({
      query: (body) => ({
        url: "/api/revenue/get-past-management-fee/",
        method: "POST",
        body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
  }),
});

export const {
  useGetFeeCalculationsQuery,
  useGetFeeCalculationByIdQuery,
  useDeleteFeeCalculationMutation,
  useCreateFeeCalculationMutation,
  useSaveFeeCalculationMutation,
  useCreateProformaMutation,
  useMinesFeeMutation,
} = feeCalculationApi;
